import { useCallback, useEffect, useRef } from 'react'
import { payPeriodKeys } from '@/hooks/queries/usePayPeriodsQuery'
import { queryKeys } from '@/hooks/query-keys'
import { useOrganizationStore } from '@/store/organization.store'
import { useQueryClient } from '@tanstack/react-query'

import type { PaginatedResponse } from '@scrambl/shared'
import { SocketEvent, TimeEntry } from '@scrambl/shared'

import { useRealtime } from '../core/useRealtime'

// import { useAuth } from '@/store/auth.store'; // Not needed until user rooms are implemented

// Add time entry events to broadcast types
export enum TimeEntryBroadcastEventType {
  TIME_ENTRY_STARTED = 'time_entry:started',
  TIME_ENTRY_STOPPED = 'time_entry:stopped',
  TIME_ENTRY_PAUSED = 'time_entry:paused',
  TIME_ENTRY_RESUMED = 'time_entry:resumed',
  TIME_ENTRY_UPDATED = 'time_entry:updated',
  TIME_ENTRY_DELETED = 'time_entry:deleted',
}

interface TimeEntryRealtimeOptions {
  onTimerStarted?: (timeEntry: TimeEntry) => void
  onTimerStopped?: (timeEntry: TimeEntry) => void
  onTimerPaused?: (timeEntry: TimeEntry) => void
  onTimerResumed?: (timeEntry: TimeEntry) => void
  onTimerUpdated?: (timeEntry: TimeEntry) => void
  onTimerDeleted?: (data: { id: string; userId: string }) => void
  enabled?: boolean
  enableBroadcastChannel?: boolean
  autoInvalidate?: boolean
}

/**
 * Hook for real-time time entry synchronization
 * Listens to timer events for the current user via socket events and broadcast channels
 */
export function useTimeEntryRealtime(options: TimeEntryRealtimeOptions = {}) {
  const {
    onTimerStarted,
    onTimerStopped,
    onTimerPaused,
    onTimerResumed,
    onTimerUpdated,
    onTimerDeleted,
    enabled = true,
    enableBroadcastChannel = true,
    autoInvalidate = true,
  } = options
  const queryClient = useQueryClient()
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )

  // const { user } = useAuth.getState(); // Not needed until user rooms are implemented
  // const userId = user?.id; // Not needed until user rooms are implemented

  const {
    socket,
    isReady,
    addEventListener,
    processSocketEvent,
    hasProcessedEvent,
    markEventProcessed,
  } = useRealtime({
    enableBroadcastChannel,
    suppressLogging: true, // Time entries update frequently, suppress logging
  })

  // Keep refs for callbacks to avoid re-subscribing
  const callbackRefs = useRef({
    onTimerStarted,
    onTimerStopped,
    onTimerPaused,
    onTimerResumed,
    onTimerUpdated,
    onTimerDeleted,
  })

  // Update refs when callbacks change
  useEffect(() => {
    callbackRefs.current = {
      onTimerStarted,
      onTimerStopped,
      onTimerPaused,
      onTimerResumed,
      onTimerUpdated,
      onTimerDeleted,
    }
  }, [
    onTimerStarted,
    onTimerStopped,
    onTimerPaused,
    onTimerResumed,
    onTimerUpdated,
    onTimerDeleted,
  ])

  const invalidateTimeEntryQueries = useCallback(() => {
    if (!autoInvalidate) {
      return
    }

    void queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all })
    void queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
    void queryClient.invalidateQueries({ queryKey: queryKeys.payPeriods.all })
  }, [autoInvalidate, queryClient])

  const removeDeletedTimeEntryFromCache = useCallback(
    (timeEntryId: string) => {
      if (!autoInvalidate) {
        return
      }

      const removeDeletedEntries = (cached: unknown) => {
        if (!cached) return cached

        if (Array.isArray(cached)) {
          return cached.filter(entry => !entry?.id || entry.id !== timeEntryId)
        }

        if (typeof cached === 'object' && cached !== null && 'data' in cached) {
          const paginated = cached as PaginatedResponse<TimeEntry>
          if (!Array.isArray(paginated.data)) {
            return cached
          }

          return {
            ...paginated,
            data: paginated.data.filter(entry => entry.id !== timeEntryId),
          }
        }

        return cached
      }

      queryClient.setQueriesData(
        { queryKey: queryKeys.timeEntries.lists() },
        removeDeletedEntries,
      )
      queryClient.setQueriesData(
        { queryKey: [...queryKeys.timeEntries.all, 'team'] },
        removeDeletedEntries,
      )
      queryClient.removeQueries({
        queryKey: queryKeys.timeEntries.detail(timeEntryId),
        exact: true,
      })
      queryClient.setQueryData(
        queryKeys.timeEntries.active(organizationId),
        (cached: TimeEntry | null | undefined) =>
          cached?.id === timeEntryId ? null : cached,
      )
    },
    [autoInvalidate, organizationId, queryClient],
  )

  // Socket event handlers
  const handleTimerStarted = useCallback(
    (data: TimeEntry) => {
      processSocketEvent(
        SocketEvent.TIME_ENTRY_STARTED,
        'time_entry',
        data.id,
        data,
      )
      invalidateTimeEntryQueries()
      callbackRefs.current.onTimerStarted?.(data)
    },
    [invalidateTimeEntryQueries, processSocketEvent],
  )

  const handleTimerStopped = useCallback(
    (data: TimeEntry) => {
      processSocketEvent(
        SocketEvent.TIME_ENTRY_STOPPED,
        'time_entry',
        data.id,
        data,
      )
      invalidateTimeEntryQueries()
      callbackRefs.current.onTimerStopped?.(data)
    },
    [invalidateTimeEntryQueries, processSocketEvent],
  )

  const handleTimerPaused = useCallback(
    (data: TimeEntry) => {
      processSocketEvent(
        SocketEvent.TIME_ENTRY_PAUSED,
        'time_entry',
        data.id,
        data,
      )
      invalidateTimeEntryQueries()
      callbackRefs.current.onTimerPaused?.(data)
    },
    [invalidateTimeEntryQueries, processSocketEvent],
  )

  const handleTimerResumed = useCallback(
    (data: TimeEntry) => {
      processSocketEvent(
        SocketEvent.TIME_ENTRY_RESUMED,
        'time_entry',
        data.id,
        data,
      )
      invalidateTimeEntryQueries()
      callbackRefs.current.onTimerResumed?.(data)
    },
    [invalidateTimeEntryQueries, processSocketEvent],
  )

  const handleTimerUpdated = useCallback(
    (data: TimeEntry) => {
      processSocketEvent(
        SocketEvent.TIME_ENTRY_UPDATED,
        'time_entry',
        data.id,
        data,
      )
      invalidateTimeEntryQueries()
      callbackRefs.current.onTimerUpdated?.(data)
    },
    [invalidateTimeEntryQueries, processSocketEvent],
  )

  const handleTimerDeleted = useCallback(
    (data: { id: string; userId: string }) => {
      processSocketEvent(
        SocketEvent.TIME_ENTRY_DELETED,
        'time_entry',
        data.id,
        data,
      )
      removeDeletedTimeEntryFromCache(data.id)
      invalidateTimeEntryQueries()
      callbackRefs.current.onTimerDeleted?.(data)
    },
    [
      invalidateTimeEntryQueries,
      processSocketEvent,
      removeDeletedTimeEntryFromCache,
    ],
  )

  // Set up socket listeners
  useEffect(() => {
    if (!enabled || !socket || !isReady) {
      return
    }

    // Add socket event listeners
    socket.on(SocketEvent.TIME_ENTRY_STARTED, handleTimerStarted)
    socket.on(SocketEvent.TIME_ENTRY_STOPPED, handleTimerStopped)
    socket.on(SocketEvent.TIME_ENTRY_PAUSED, handleTimerPaused)
    socket.on(SocketEvent.TIME_ENTRY_RESUMED, handleTimerResumed)
    socket.on(SocketEvent.TIME_ENTRY_UPDATED, handleTimerUpdated)
    socket.on(SocketEvent.TIME_ENTRY_DELETED, handleTimerDeleted)

    return () => {
      socket.off(SocketEvent.TIME_ENTRY_STARTED, handleTimerStarted)
      socket.off(SocketEvent.TIME_ENTRY_STOPPED, handleTimerStopped)
      socket.off(SocketEvent.TIME_ENTRY_PAUSED, handleTimerPaused)
      socket.off(SocketEvent.TIME_ENTRY_RESUMED, handleTimerResumed)
      socket.off(SocketEvent.TIME_ENTRY_UPDATED, handleTimerUpdated)
      socket.off(SocketEvent.TIME_ENTRY_DELETED, handleTimerDeleted)
    }
  }, [
    enabled,
    socket,
    isReady,
    handleTimerStarted,
    handleTimerStopped,
    handleTimerPaused,
    handleTimerResumed,
    handleTimerUpdated,
    handleTimerDeleted,
  ])

  // Set up broadcast channel listeners
  useEffect(() => {
    if (!enabled || !enableBroadcastChannel) {
      return
    }

    // Add broadcast event listeners for cross-tab sync
    const cleanupFns: (() => void)[] = []

    cleanupFns.push(
      addEventListener(TimeEntryBroadcastEventType.TIME_ENTRY_STARTED, data => {
        const timeEntry = data as TimeEntry
        // Skip if already processed via socket
        if (
          hasProcessedEvent(`${SocketEvent.TIME_ENTRY_STARTED}-${timeEntry.id}`)
        ) {
          return
        }
        markEventProcessed(
          `broadcast-${SocketEvent.TIME_ENTRY_STARTED}-${timeEntry.id}`,
        )
        invalidateTimeEntryQueries()
        callbackRefs.current.onTimerStarted?.(timeEntry)
      }),
    )

    cleanupFns.push(
      addEventListener(TimeEntryBroadcastEventType.TIME_ENTRY_STOPPED, data => {
        const timeEntry = data as TimeEntry
        if (
          hasProcessedEvent(`${SocketEvent.TIME_ENTRY_STOPPED}-${timeEntry.id}`)
        ) {
          return
        }
        markEventProcessed(
          `broadcast-${SocketEvent.TIME_ENTRY_STOPPED}-${timeEntry.id}`,
        )
        invalidateTimeEntryQueries()
        callbackRefs.current.onTimerStopped?.(timeEntry)
      }),
    )

    cleanupFns.push(
      addEventListener(TimeEntryBroadcastEventType.TIME_ENTRY_PAUSED, data => {
        const timeEntry = data as TimeEntry
        if (
          hasProcessedEvent(`${SocketEvent.TIME_ENTRY_PAUSED}-${timeEntry.id}`)
        ) {
          return
        }
        markEventProcessed(
          `broadcast-${SocketEvent.TIME_ENTRY_PAUSED}-${timeEntry.id}`,
        )
        invalidateTimeEntryQueries()
        callbackRefs.current.onTimerPaused?.(timeEntry)
      }),
    )

    cleanupFns.push(
      addEventListener(TimeEntryBroadcastEventType.TIME_ENTRY_RESUMED, data => {
        const timeEntry = data as TimeEntry
        if (
          hasProcessedEvent(`${SocketEvent.TIME_ENTRY_RESUMED}-${timeEntry.id}`)
        ) {
          return
        }
        markEventProcessed(
          `broadcast-${SocketEvent.TIME_ENTRY_RESUMED}-${timeEntry.id}`,
        )
        invalidateTimeEntryQueries()
        callbackRefs.current.onTimerResumed?.(timeEntry)
      }),
    )

    cleanupFns.push(
      addEventListener(TimeEntryBroadcastEventType.TIME_ENTRY_UPDATED, data => {
        const timeEntry = data as TimeEntry
        if (
          hasProcessedEvent(`${SocketEvent.TIME_ENTRY_UPDATED}-${timeEntry.id}`)
        ) {
          return
        }
        markEventProcessed(
          `broadcast-${SocketEvent.TIME_ENTRY_UPDATED}-${timeEntry.id}`,
        )
        invalidateTimeEntryQueries()
        callbackRefs.current.onTimerUpdated?.(timeEntry)
      }),
    )

    cleanupFns.push(
      addEventListener(TimeEntryBroadcastEventType.TIME_ENTRY_DELETED, data => {
        const deleteData = data as { id: string; userId: string }
        if (
          hasProcessedEvent(
            `${SocketEvent.TIME_ENTRY_DELETED}-${deleteData.id}`,
          )
        ) {
          return
        }
        markEventProcessed(
          `broadcast-${SocketEvent.TIME_ENTRY_DELETED}-${deleteData.id}`,
        )
        removeDeletedTimeEntryFromCache(deleteData.id)
        invalidateTimeEntryQueries()
        callbackRefs.current.onTimerDeleted?.(deleteData)
      }),
    )

    return () => {
      cleanupFns.forEach(cleanup => cleanup())
    }
  }, [
    addEventListener,
    enabled,
    enableBroadcastChannel,
    hasProcessedEvent,
    invalidateTimeEntryQueries,
    markEventProcessed,
    removeDeletedTimeEntryFromCache,
  ])

  return {
    isReady,
  }
}
